import React, { useContext, useState } from 'react';
import { Grid, TextField } from '@material-ui/core';
import { PlanningContext } from '../context';
import { useApolloClient, useMutation } from '@apollo/client';
import { MODALS } from '../constants';
import { Body2, GridItemRow, ModalActions, ModalContent, ModalTitle, PrimaryButton, SecondaryButton } from '../blocks';
import * as Sentry from '@sentry/react';
import { RESCHEDULE_ROUTE } from '../graphql/mutations';
import { QUERY_PLANNING_DATA } from '../graphql/queries';
import { buildOptimisticRescheduleResponse } from '../graphql/optimisticResponses';
import { matchesOrderCondition } from '../graphql/planningBuckets';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { genAttributes } from '@onward-delivery/core';
import { asDateInTZ } from '@/utilities/convertToISO';
import { css } from '@emotion/react';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import RescheduleReasonFields from '@/components/Reschedule/RescheduleReasonFields';
import useRescheduleReason from '@/components/Reschedule/useRescheduleReason';
import { useBusinessTimezone } from '@/hooks';
import { isResourceAcceptingBookings } from '../planningDerivations';
import { shutoffMessage } from '../util/resourceCeilings';
import { useResourcesById } from '../hooks/useResourcesById';

const RouteRescheduleModal = () => {
    const {
        modalOpen,
        setModalOpen,
        actionableRoute,
        deliveryDate,
        selectedRoute,
        setSelectedRoute,
        setError,
        setNotification,
        state: { variables },
    } = useContext(PlanningContext);

    const businessTz = useBusinessTimezone();

    const routeResource = useResourcesById()[actionableRoute?.resource_id];

    const [newDeliveryDate, setNewDeliveryDate] = useState(deliveryDate);
    const { patch: reschedulePatch, isValid, fieldProps } = useRescheduleReason();

    const [rescheduleRoute, { loading }] = useMutation(RESCHEDULE_ROUTE);
    const client = useApolloClient();

    const handleModalClose = () => {
        if (selectedRoute === actionableRoute?.route_id) {
            setSelectedRoute(null);
        }
        setModalOpen(null);
    };

    const handleRescheduleRoute = () => {
        const order = actionableRoute?.orders?.[0]?.order || {};
        const { zip } = genAttributes(order);
        const tz = zipcode_to_timezone.lookup(order[zip]) || Intl.DateTimeFormat().resolvedOptions().timeZone;

        const pickups = actionableRoute.orders.filter((mapping) => mapping.type === 'PICKUP');
        const dropoffs = actionableRoute.orders.filter((mapping) => mapping.type !== 'PICKUP');
        const deliveryDateISO = asDateInTZ(newDeliveryDate, tz).toISOString();

        // The orders keep their resource, so moving the day re-books it on the destination day:
        // refuse when the resource is shut off there. Capacity there isn't checked — the board only
        // holds the current day's routes.
        if (routeResource && !isResourceAcceptingBookings(routeResource, deliveryDateISO, businessTz)) {
            setNotification({
                severity: 'warning',
                message: shutoffMessage(actionableRoute),
            });
            return;
        }

        // The server keeps the route in QUERY_PLANNING_DATA while
        // from_date <= scheduled_delivery < to_date, over the exact instant
        // the mutation writes (asDateInTZ in the route's tz) — a day-string
        // compare against the plan date diverges from that whenever the tz
        // conversion crosses the window bounds.
        const scheduled = new Date(deliveryDateISO);
        const routeStays = scheduled >= new Date(variables.from_date) && scheduled < new Date(variables.to_date);

        // The mutation also rewrites pickup_date / delivery_date on every
        // mapped order, and non-DISCARD-mapped orders can simultaneously be
        // bucket rows — re-evaluate their bucket membership against the
        // query's mirrored where-clauses (all mapped orders get the same new
        // date, so one evaluation per leg covers them).
        const stayPickup = matchesOrderCondition({ pickup_date: deliveryDateISO }, variables.pickupCondition);
        const stayDropoff = matchesOrderCondition({ delivery_date: deliveryDateISO }, variables.dropoffCondition);
        const pickupIds = new Set(pickups.map((mapping) => mapping.order_id));
        const dropoffIds = new Set(dropoffs.map((mapping) => mapping.order_id));

        // False when the cached planning query was unreadable — fall back to a
        // network-only read in onCompleted instead of guessing.
        let lastPatchApplied = false;

        rescheduleRoute({
            // Fires from the live board and can race assigns/stop edits on the
            // same route — serialize with the board's other stop mutations.
            context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
            variables: {
                route_id: actionableRoute?.route_id,
                delivery_date: deliveryDateISO,
                pickup_ids: pickups.map((mapping) => mapping.order_id),
                dropoff_ids: dropoffs.map((mapping) => mapping.order_id),
                ...reschedulePatch,
            },
            optimisticResponse: buildOptimisticRescheduleResponse({
                route: actionableRoute,
                deliveryDateISO,
                pickups,
                dropoffs,
            }),
            // The returning updates entity fields in place; this only handles
            // list MEMBERSHIP — drop the route when its new scheduled_delivery
            // leaves the query window, and drop bucket rows whose rewritten
            // leg date leaves theirs.
            update: (cache) => {
                cache.updateQuery({ query: QUERY_PLANNING_DATA, variables }, (prev) => {
                    if (!prev?.routes) {
                        lastPatchApplied = false;
                        return prev;
                    }
                    lastPatchApplied = true;
                    return {
                        ...prev,
                        routes: routeStays
                            ? prev.routes
                            : prev.routes.filter((r) => r.route_id !== actionableRoute.route_id),
                        pickups: stayPickup
                            ? prev.pickups
                            : (prev.pickups || []).filter((o) => !pickupIds.has(o.order_id)),
                        dropoffs: stayDropoff
                            ? prev.dropoffs
                            : (prev.dropoffs || []).filter((o) => !dropoffIds.has(o.order_id)),
                    };
                });
            },
            onCompleted: () => {
                if (!lastPatchApplied) {
                    client
                        .query({
                            query: QUERY_PLANNING_DATA,
                            variables,
                            fetchPolicy: 'network-only',
                            context: { queryDeduplication: false },
                        })
                        .catch(() => {});
                }
                handleModalClose();
            },
            onError: (error) => {
                setError(error, 'Error rescheduling route');
            },
        });
    };

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ROUTE_RESCHEDULE} onClose={handleModalClose}>
            <ModalTitle>{`Reschedule Route ${actionableRoute?.route_number}`}</ModalTitle>
            <ModalContent>
                <GridItemRow>
                    <Grid container>
                        <Body2>Select a new delivery date for this route.</Body2>
                        <Body2>Timeframes will not be saved.</Body2>
                    </Grid>
                    <TextField
                        type="date"
                        variant="outlined"
                        disabled={loading}
                        value={newDeliveryDate}
                        onChange={(e) => setNewDeliveryDate(e.target.value)}
                        css={css`
                            input {
                                font-size: 14px;
                                font-weight: 400;
                                padding: 8px 12px 12px 12px;
                            }
                        `}
                    />
                </GridItemRow>
                <GridItemRow>
                    <RescheduleReasonFields {...fieldProps} disabled={loading} />
                </GridItemRow>
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading} onClick={handleModalClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={loading || !isValid} onClick={handleRescheduleRoute}>
                    Reschedule
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default RouteRescheduleModal;
